// Minimal typings for the Cybersource Flex Microform v2 client library.

export type FlexFieldType = 'number' | 'securityCode';

export type FlexFieldEvent = 'load' | 'unload' | 'focus' | 'blur' | 'change' | 'autocomplete' | 'inputSubmitRequest';

export interface FlexField {
  load(container: string | HTMLElement): void;
  unload(): void;
  clear(): void;
  on(event: FlexFieldEvent, handler: (data?: unknown) => void): void;
}

export interface FlexError {
  reason: string;
  message: string;
  details?: Array<{ location: string; message: string }>;
}

export interface FlexTokenOptions {
  expirationMonth: string;
  expirationYear: string;
}

export interface FlexMicroform {
  createField(type: FlexFieldType, options?: { placeholder?: string }): FlexField;
  createToken(options: FlexTokenOptions, callback: (error: FlexError | null, token: string) => void): void;
}

export interface FlexInstance {
  microform(options?: { styles?: Record<string, Record<string, string>> }): FlexMicroform;
}

export type FlexConstructor = new (captureContext: string) => FlexInstance;

declare global {
  interface Window {
    Flex?: FlexConstructor;
  }
}

let libraryPromise: Promise<FlexConstructor> | null = null;

/**
 * Loads the Flex client library once per page, no matter how many widgets are on it.
 * A failed load is forgotten so a later widget can retry.
 */
export function loadFlexLibrary(src: string, integrity: string): Promise<FlexConstructor> {
  if (window.Flex) {
    return Promise.resolve(window.Flex);
  }

  libraryPromise ??= new Promise<FlexConstructor>((resolve, reject) => {
    const script = document.createElement('script');
    const fail = (message: string) => {
      script.remove();
      libraryPromise = null;
      reject(new Error(message));
    };

    script.src = src;
    script.integrity = integrity;
    script.crossOrigin = 'anonymous';
    script.async = true;
    script.onload = () => (window.Flex ? resolve(window.Flex) : fail('Payment library loaded without Flex.'));
    script.onerror = () => fail('Failed to load the payment library.');

    document.head.appendChild(script);
  });

  return libraryPromise;
}
